<script setup lang="ts">
import { useSeoMeta } from "@unhead/vue";
import { formatCurrency } from "shared-core/utils/common.ts";
import riskAnalysisHeroIllustration from "shared-ui/assets/illustrations/risk-analysis-hero.avif";
import platformTasksImage from "shared-ui/assets/images/platform-tasks.avif";
import platformTasksMobileImage from "shared-ui/assets/images/platform-tasks-mobile.avif";
import WebsiteFooter from "shared-ui/components/layout/WebsiteFooter.vue";
import WebsiteHeader from "shared-ui/components/layout/WebsiteHeader.vue";
import { useRiskAnalysisStore } from "shared-ui/stores";
import { firstQueryValue } from "shared-ui/utils/router-utils";
import { onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import AverageStatsCard from "@/components/cards/risk-analysis/AverageStatsCard.vue";
import type DomainForm from "@/components/risk-analysis/DomainForm.vue";
import LoginLayout from "@/layouts/LoginLayout.vue";

const riskAnalysisStore = useRiskAnalysisStore();
const route = useRoute();

const domain = ref<string>("");
const isSubmitted = ref<boolean>(false);
const errorMessage = ref<string>("");
const isFreeRiskAnalysis = ref<boolean>(false);
const domainFormRef = ref<{ onSubmit: () => Promise<void> } | null>(null);

const attackCount = ref<number>(getCurrentAttackCount());
let attackTimer: number | null = null;
const contentClasses =
  "mx-auto flex w-full max-w-7xl flex-col justify-center gap-24 px-8 mt-20";

const averageBreachCost = 4_880_000;
const attacksPerDay = 5_000_000_000;
const secondsPerDay = 86400;
const websiteBase = import.meta.env.VITE_PUBLIC_SITE_URL;
const router = useRouter();

definePage({
  meta: {
    requiresAuth: false,
  },
});

const { t } = useI18n();

useSeoMeta({
  title: () => t("risk_analysis.public.title"),
});

onMounted(async () => {
  domain.value = firstQueryValue(route.query.domain) ?? "";

  if (firstQueryValue(route.query.freeRiskAnalysis) !== null) {
    riskAnalysisStore.setRequireEmail(true);
    isFreeRiskAnalysis.value = true;
  } else {
    riskAnalysisStore.setRequireEmail(false);
    isFreeRiskAnalysis.value = false;
  }

  attackTimer = window.setInterval(() => {
    attackCount.value +=
      Math.floor(Math.random() * (attacksPerDay / secondsPerDay)) + 1;
  }, 1000);

  try {
    await riskAnalysisStore.fetchConversionRate(
      navigator.languages[0] ?? navigator.language ?? "en-US",
    );
  } catch (error) {
    console.error("Failed to fetch conversion rate", error);
  }

  if (firstQueryValue(route.query.startRiskAnalysis) === "true") {
    await domainFormRef.value?.onSubmit();
  }
});

onUnmounted(() => {
  abortAnalysis();
  if (attackTimer) clearInterval(attackTimer);
});

function abortAnalysis(): void {
  reset();
  riskAnalysisStore.abortRiskAnalysis();
}

function reset(): void {
  isSubmitted.value = false;
  domain.value = "";
  errorMessage.value = "";
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat(navigator.language).format(n);
}

function getCurrentAttackCount(): number {
  const dateNow = new Date();
  const dateMidnight = new Date(
    dateNow.getFullYear(),
    dateNow.getMonth(),
    dateNow.getDate(),
    0,
    0,
    0,
  );
  const secondsPassed = (dateNow.getTime() - dateMidnight.getTime()) / 1000;

  return Math.round(attacksPerDay * (secondsPassed / secondsPerDay));
}
</script>

<template>
  <!-- Use v-show because DomainForm needs to be mounted at all times -->
  <div v-show="!isSubmitted" class="min-h-screen bg-white">
    <div class="relative min-h-screen">
      <!-- Landing -->
      <div class="mx-auto">
        <div class="relative isolate w-full overflow-hidden">
          <div class="absolute inset-0 -z-10 h-full">
            <div
              class="absolute bottom-0 left-0 h-72 w-full bg-linear-to-r from-brand-red from-15% via-brand-blue via-60% to-brand-green to-85% mask-fade-in-early blur-sm"
            ></div>
          </div>

          <WebsiteHeader
            :home-link="websiteBase"
            :product-link="`${websiteBase}/en/product`"
            :pricing-link="`${websiteBase}/en/pricing`"
            :partners-link="`${websiteBase}/en/partners`"
            :company-link="`${websiteBase}/en/company`"
            :security-link="`${websiteBase}/en/security`"
          />

          <div class="relative z-10 px-8">
            <header class="flex w-full justify-center">
              <div
                class="z-10 inline-grid w-full max-w-7xl grid-cols-1 place-items-center gap-6 lg:grid-cols-2"
              >
                <h1 class="text-6xl! font-normal! md:text-center">
                  {{ t("risk_analysis.public.get_free_analysis") }}
                </h1>
                <div class="inline-flex max-w-3xl flex-col gap-6">
                  <p class="inline-block text-xl font-normal">
                    {{ t("risk_analysis.public.enter_web_address") }}
                  </p>
                  <div
                    class="inline-grid w-full grid-cols-1 items-center justify-center gap-3 sm:inline-flex sm:flex-row sm:gap-5 md:items-start md:justify-start"
                  >
                    <InfoCheck
                      :info="t('risk_analysis.public.cyber_risk_level')"
                    />
                    <InfoCheck :info="t('risk_analysis.public.top_threats')" />
                    <InfoCheck
                      :info="t('risk_analysis.public.breach_cost_estimates')"
                    />
                  </div>
                </div>
              </div>
            </header>

            <main
              class="inline-flex min-h-200 w-full items-center justify-center"
            >
              <div
                class="relative inline-flex min-h-160 w-full flex-col items-center justify-center overflow-hidden rounded-4xl"
              >
                <div
                  class="absolute inset-0 bg-cover bg-center"
                  :style="{
                    backgroundImage: `url(${riskAnalysisHeroIllustration})`,
                  }"
                ></div>
                <div class="absolute inset-0 bg-black/30"></div>
                <DomainForm
                  ref="domainFormRef"
                  v-model:domain="domain"
                  v-model:error-message="errorMessage"
                  v-model:is-submitted="isSubmitted"
                  class="relative z-10 inline-flex size-full min-h-160 flex-col items-center justify-center px-4"
                  @analyzing-failed="reset"
                />
              </div>
            </main>
          </div>
        </div>

        <div :class="contentClasses">
          <div class="flex w-full flex-col gap-8">
            <h2 class="text-4xl! font-normal!">
              {{ t("risk_analysis.public.attacks_easy_expensive") }}
            </h2>
            <section
              class="grid w-full grid-cols-1 items-start justify-center gap-12 text-left md:grid-cols-2 md:gap-9"
            >
              <AverageStatsCard
                :header="t('risk_analysis.public.global_attacks_today')"
                :stat="formatNumber(attackCount)"
              >
                <template #explanation-slot>
                  {{ t("risk_analysis.public.attacks_explanation") }}
                  <SourcePopover
                    :sources="[
                      {
                        name: 'Cost of a Data Breach Report 2025 | IBM',
                        link: 'https://www.ibm.com/reports/data-breach',
                      },
                      {
                        name: 'Malware | AVTEST',
                        link: 'https://www.av-test.org/en/statistics/malware/',
                      },
                      {
                        name: '100 Facts, Figures, Predictions And Statistics | Cybercrime Magazine',
                        link: 'https://cybersecurityventures.com/cybersecurity-almanac-2022/',
                      },
                    ]"
                  />
                </template>
              </AverageStatsCard>
              <AverageStatsCard
                :header="t('risk_analysis.public.avg_cost_per_breach')"
                :stat="
                  formatCurrency(
                    averageBreachCost *
                      riskAnalysisStore.currencyConversion.rate,
                    riskAnalysisStore.currencyConversion.currency,
                  )
                "
              >
                <template #explanation-slot>
                  {{ t("risk_analysis.public.breach_cost_explanation") }}
                  <SourcePopover
                    :sources="[
                      {
                        name: 'Study Finds Average Cost of Data Breaches Significantly Increased Globally in 2024',
                        link: 'https://www.morganlewis.com/blogs/sourcingatmorganlewis/2025/05/study-finds-average-cost-of-data-breaches-significantly-increased-globally-in-2024',
                      },
                    ]"
                  />
                </template>
              </AverageStatsCard>
            </section>
          </div>

          <div v-if="!isFreeRiskAnalysis" class="flex w-full flex-col gap-8">
            <h2 class="text-4xl! font-normal!">
              {{ t("risk_analysis.public.see_how_secure") }}
            </h2>
            <p>
              {{ t("risk_analysis.public.controla_description") }}
            </p>
          </div>
        </div>
        <div
          v-if="!isFreeRiskAnalysis"
          class="flex w-full justify-center pt-8 md:px-8"
        >
          <div class="hidden max-w-7xl px-8 md:block">
            <img
              :src="platformTasksImage"
              class="h-auto w-full rounded-3xl"
              alt=""
              loading="lazy"
              width="1064"
              height="688"
            />
          </div>
          <img
            :src="platformTasksMobileImage"
            class="block h-auto w-full rounded-3xl md:hidden"
            alt=""
            loading="lazy"
            width="698"
            height="619"
          />
        </div>

        <div :class="contentClasses">
          <div v-if="!isFreeRiskAnalysis" class="flex w-full flex-col gap-8">
            <h2 class="mt-16 text-4xl! font-normal!">
              {{ t("risk_analysis.public.get_full_cybersecurity") }}
            </h2>
            <div class="grid grid-cols-1 gap-12 md:grid-cols-3">
              <div class="flex flex-col gap-3">
                <h3 class="text-xl! font-medium!">
                  {{ t("risk_analysis.public.scans_environment") }}
                </h3>
                <p class="text-lg">
                  {{ t("risk_analysis.public.scans_environment_desc") }}
                </p>
              </div>
              <div class="flex flex-col gap-3">
                <h3 class="text-xl! font-medium!">
                  {{ t("risk_analysis.public.risks_handled_automatically") }}
                </h3>
                <p class="text-lg">
                  {{ t("risk_analysis.public.risks_handled_desc") }}
                </p>
              </div>
              <div class="flex flex-col gap-3">
                <h3 class="text-xl! font-medium!">
                  {{ t("risk_analysis.public.clear_security_overview") }}
                </h3>
                <p class="text-lg">
                  {{ t("risk_analysis.public.clear_security_overview_desc") }}
                </p>
              </div>
            </div>
          </div>

          <section class="flex w-full flex-col gap-8">
            <TrustedBy />
          </section>
        </div>

        <WebsiteFooter
          :product-link="`${websiteBase}/en/product`"
          :pricing-link="`${websiteBase}/en/pricing`"
          :resource-link="`${websiteBase}/en/resources`"
          :partners-link="`${websiteBase}/en/partners`"
          :company-link="`${websiteBase}/en/company`"
          :public-risk-analysis-link="`${router.resolve('/risk-analysis/public').href}/?freeRiskAnalysis=true`"
          :agent-download-link="`${websiteBase}/en/download-app`"
          :privacy-policy-link="`${websiteBase}/en/privacy-policy`"
          :terms-and-conditions-link="`${websiteBase}/en/terms-and-conditions`"
        />
      </div>
    </div>
  </div>
  <!-- Analysing -->
  <LoginLayout v-if="isSubmitted">
    <template #default>
      <RiskAnalysisLoading :domain="domain" @stop-analyzing="abortAnalysis" />
    </template>
  </LoginLayout>
</template>
